/**
 * Unified Translate Global Tool
 *
 * Translates content to any specified target language using Claude.
 * Automatically detects the source language.
 * Handles research reports, formatted reports, and video scripts
 * with streaming output and PDF/DOCX generation.
 */

import { buildModelDisplay } from '../modelDisplay';
import {
  runTranslation,
  formatTranslationError,
  buildTranslationManifest,
  formatManifestForTranslation,
  parseTranslatedManifest,
  extractBodyContentFromPartialTranslation,
  translateDocumentTranscription,
  isRefusalShapedTranslation,
} from '../../translator';
import { generateDualFormatDocumentsWithStorage } from '../../../utils/documentGenerator';
import { assessTranslationCompleteness } from './translationCompleteness';
import { fetchCurrentTenantBranding } from '../../tenantBranding';
import { getAuth } from 'firebase/auth';
import type { SerializedAttachment } from '../../../components/GlobalChatSystem/types';
import {
  CLAUDE_TRANSLATOR_DISPLAY,
  CLAUDE_DOCUMENT_ANALYSIS_DISPLAY,
  TRANSLATION_TOTAL_TIMEOUT_MS,
  TRANSLATION_TIMEOUT_DESCRIPTION,
} from '../../../config/agenticWorkflowConfig';
import { isAgenticWorkflowsAuthorized } from '../../agenticWorkflows';
import { formatResearchToHtml, formatDocumentToHtml, isDocumentContent, generateSpecialMessageTemplate, parseTranslationPrompt } from '../../agenticWorkflows/helpers';
import {
  getPendingWidgetHtml,
  getStreamingTailHtml,
  getSectionProgressHtml,
  getTranslationCoverageHtml,
} from '../widgetHtml';
import { analyzeDocumentsWithClaude, hasAnalyzableFiles, extractPdfImagesClientSide } from '../../claudeDocumentAnalyzer';
import type { SourcePageImage } from '../../claudeDocumentAnalyzer';
import { computeAttachmentFingerprint } from '../documentReader';
import { finalizeAttachmentReadComplete } from '../messaging';
import { getLatestOutputBySource, storeWorkflowOutput, getLatestDocumentAnalysis } from '../workflowOutputStore';
import { hasNegationAround } from '../triggerHelpers';

import type {
  GlobalToolDefinition,
  StepContext,
  StepResult,
  ToolTheme,
  TokenUsageMetadata,
} from '../types';

// ============================================================================
// Model Display Name Helper
// ============================================================================

// buildModelDisplay: consolidated into ../modelDisplay (2026-06-11) — single
// recording-time codename builder shared by orchestrator + all tools.

// ============================================================================
// Tool State
// ============================================================================

/**
 * Parsed sections from document analysis content.
 */
export interface DocumentAnalysisSections {
  /** Metadata extracted from document analysis */
  metadata: {
    filename?: string;
    type?: string;
    language?: string;
    pages?: string;
  };
  /** English description and summary (always in English) */
  englishSummary: string;
  /** Original language transcription (needs translation if not target language) */
  originalTranscription: string;
}

/**
 * State for the unified translate tool.
 */
export interface TranslateState {
  /** Whether translation is currently in progress */
  isTranslating: boolean;
  /** Target language for translation (e.g., "Spanish", "French", "Japanese") */
  targetLanguage?: string;
  /** Detected source language */
  detectedSourceLanguage?: string;
  /** The translated content */
  translatedContent?: string;
  /** Translated title */
  translatedTitle?: string;
  /** Source type of the content */
  sourceType?: 'research' | 'formatted' | 'video_script' | 'document_analysis' | 'document_analysis_failed' | 'inline' | 'attachment';
  /** Token usage from translation */
  tokenUsage?: TokenUsageMetadata;
  /** Content mode used for this translation */
  contentMode?: 'inline' | 'attachment' | 'shared_context';
  /** Whether content is from document analysis (has 2-section structure) */
  isDocumentAnalysisContent?: boolean;
  /** Parsed sections from document analysis */
  documentSections?: DocumentAnalysisSections;
}

// ============================================================================
// Document Analysis Detection Helpers
// ============================================================================

/**
 * Detect if content is from document analyzer.
 * Supports both:
 * - NEW format: "This is a [type] ([filename], [pages]) in [language]. [description]\n\n[transcription]"
 * - OLD format: "1. DOCUMENT DESCRIPTION & SUMMARY ... 2. FULL VERBATIM TRANSCRIPTION ..."
 */
function isDocumentAnalysisContent(content: string): boolean {
  // NEW format: Starts with "This is a [type]" pattern followed by metadata in parentheses
  // Example: "This is a PDF document (report.pdf, 2 pages) in Spanish."
  const hasNewFormat = /^This is an? [a-zA-Z]+\s*(?:document|image|file)?\s*\([^)]+\)\s*in\s+[a-zA-Z]+\./i.test(content.trim());

  if (hasNewFormat) {
    // Also verify there's a double newline separator (English summary followed by transcription)
    const hasDoubleNewline = /\n\n\n|\n\n/.test(content);
    if (hasDoubleNewline) return true;
  }

  // OLD format: Check for the old section headers (for backwards compatibility)
  const hasOldDescriptionSection = /(?:#{1,3}\s*)?(?:1\.?\s*)?(?:DOCUMENT\s+)?DESCRIPTION\s*(?:&|AND)?\s*SUMMARY/i.test(content);
  const hasOldTranscriptionSection = /(?:#{1,3}\s*)?(?:2\.?\s*)?FULL\s*(?:VERBATIM\s*)?TRANSCRIPTION/i.test(content);

  return hasOldDescriptionSection && hasOldTranscriptionSection;
}

/**
 * Parse document analysis content into its constituent sections.
 * Supports both:
 * - NEW format: "This is a [type]..." paragraph followed by double newline, then transcription
 * - OLD format: Sections with "DOCUMENT DESCRIPTION & SUMMARY" and "FULL VERBATIM TRANSCRIPTION" headers
 */
function parseDocumentAnalysisSections(content: string): DocumentAnalysisSections {
  const metadata: DocumentAnalysisSections['metadata'] = {};
  let englishSummary = '';
  let originalTranscription = '';

  // Try NEW format first: "This is a [type] ([filename], [pages]) in [language]."
  const newFormatMatch = content.trim().match(/^(This is an? ([a-zA-Z]+)\s*(?:document|image|file)?\s*\(([^,]+),?\s*([^)]*)\)\s*in\s+([a-zA-Z]+)\.[^\n]*(?:\n[^\n]+)*)/i);

  if (newFormatMatch) {
    // Extract metadata from the "This is a [type] ([filename], [pages]) in [language]" pattern
    metadata.type = newFormatMatch[2]?.trim();
    metadata.filename = newFormatMatch[3]?.trim();
    metadata.pages = newFormatMatch[4]?.trim();
    metadata.language = newFormatMatch[5]?.trim();

    // Split on double newline (2+ newlines) to separate summary from transcription
    const parts = content.split(/\n{2,}/);
    if (parts.length >= 2) {
      // First part is the English summary paragraph
      englishSummary = parts[0].trim();
      // Everything after the first double newline is the transcription
      originalTranscription = parts.slice(1).join('\n\n').trim();
    } else {
      // No clear split - treat first paragraph as summary, rest as transcription
      englishSummary = newFormatMatch[0].trim();
      originalTranscription = content.slice(newFormatMatch[0].length).trim();
    }

    return {
      metadata,
      englishSummary,
      originalTranscription,
    };
  }

  // Fall back to OLD format parsing
  // Find the transcription section header to split content
  const transcriptionMatch = content.match(/(?:#{1,3}\s*)?(?:2\.?\s*)?FULL\s*(?:VERBATIM\s*)?TRANSCRIPTION[^\n]*/i);

  if (transcriptionMatch && transcriptionMatch.index !== undefined) {
    // Everything before transcription header is the summary section
    englishSummary = content.slice(0, transcriptionMatch.index).trim();
    // Everything after (including header) is the transcription
    originalTranscription = content.slice(transcriptionMatch.index).trim();
  } else {
    // If no clear split, treat entire content as needing translation
    englishSummary = '';
    originalTranscription = content;
  }

  // Extract metadata from old format - support both (**FIELD:**) and (FIELD:) formats
  const filenameMatch = englishSummary.match(/(?:\*\*)?FILE(?:\*\*)?:?\s*(.+?)(?:\n|$)/i);
  if (filenameMatch) metadata.filename = filenameMatch[1].trim();

  const typeMatch = englishSummary.match(/(?:\*\*)?TYPE(?:\*\*)?:?\s*(.+?)(?:\n|$)/i);
  if (typeMatch) metadata.type = typeMatch[1].trim();

  const languageMatch = englishSummary.match(/(?:\*\*)?LANGUAGE(?:\*\*)?:?\s*(.+?)(?:\n|$)/i);
  if (languageMatch) metadata.language = languageMatch[1].trim();

  const pagesMatch = englishSummary.match(/(?:\*\*)?PAGES?(?:\/SECTIONS)?(?:\*\*)?:?\s*(.+?)(?:\n|$)/i);
  if (pagesMatch) metadata.pages = pagesMatch[1].trim();

  return {
    metadata,
    englishSummary,
    originalTranscription,
  };
}

/**
 * Check if a language name indicates English.
 */
function isEnglishLanguage(language: string): boolean {
  const normalized = language.toLowerCase().trim();
  return normalized === 'english' || normalized === 'en' || normalized === 'eng';
}

// ============================================================================
// Tool Theme
// ============================================================================

const TRANSLATE_THEME: ToolTheme = {
  accent: '#7C3AED',      // Purple 600
  accentBg: '#EDE9FE',    // Purple 100
  border: '#a78bfa',      // Purple 400
  subtitle: '#475569',    // Slate 600
  muted: '#64748b',       // Slate 500
};

// ============================================================================
// Button IDs
// ============================================================================

export const BUTTON_IDS = {
  translate: 'global-tool-translate-btn',
};

// ============================================================================
// Trigger Pattern
// ============================================================================

/**
 * Match "translate to [language] [optional content]" pattern.
 * Uses the generalized parseTranslationPrompt helper.
 *
 * Phase 0 hardening: when the underlying NL parser declares a match, we
 * apply the in-clause negation guard against the translate anchor
 * keywords ("translate", "translation", "traduce", "traducir") so
 * phrases like "don't translate this, just answer" do not fire.
 */
const TRANSLATE_NEGATION_ANCHOR = /\b(translate|translation|traduce|traducir|traducci[oó]n)\b/i;

function matchTranslateTrigger(text: string): { matches: boolean; targetLanguage?: string; inlineContent?: string } {
  const parsed = parseTranslationPrompt(text);
  if (parsed.isTranslate) {
    if (hasNegationAround(text, TRANSLATE_NEGATION_ANCHOR)) {
      return { matches: false };
    }
    return {
      matches: true,
      targetLanguage: parsed.targetLanguage,
      inlineContent: parsed.inlineContent || undefined,
    };
  }
  return { matches: false };
}

// ============================================================================
// Tool Definition
// ============================================================================

export const translateTool: GlobalToolDefinition<TranslateState> = {
  id: 'translate',
  name: 'Translate Document',
  description: 'Translate content to any language with automatic source language detection',

  triggerPattern: matchTranslateTrigger,

  createInitialState: (): TranslateState => ({
    isTranslating: false,
    targetLanguage: undefined,
    detectedSourceLanguage: undefined,
    translatedContent: undefined,
    translatedTitle: undefined,
    sourceType: undefined,
    tokenUsage: undefined,
    contentMode: undefined,
    isDocumentAnalysisContent: undefined,
    documentSections: undefined,
  }),

  steps: [
    // ========================================================================
    // Step 1: Validate - Check for available content (supports 3 modes)
    // ========================================================================
    {
      id: 'validate',
      name: 'Validate Content',
      activeDisplayName: 'checking for content',

      execute: async (
        ctx: StepContext<TranslateState>,
        rawInput?: unknown
      ): Promise<StepResult> => {
        const input = rawInput as { targetLanguage?: string; inlineContent?: string; content?: string; isChainStep?: boolean } | undefined;
        let targetLanguage = input?.targetLanguage || 'Spanish'; // Default to Spanish for backward compat
        let inlineContent = input?.inlineContent || input?.content || '';

        // If no inline content provided, try to parse it from the trigger message
        // This handles the case where orchestrator triggers translate without extracting inline content
        if (!inlineContent.trim()) {
          const parsed = parseTranslationPrompt(ctx.triggerMessage.text);
          if (parsed.isTranslate && parsed.inlineContent) {
            inlineContent = parsed.inlineContent;
            console.log('[Translate] Extracted inline content from trigger message:', inlineContent.slice(0, 100) + '...');
          }
          // Also use parsed targetLanguage if available and not already specified by orchestrator
          if (parsed.isTranslate && parsed.targetLanguage && !input?.targetLanguage) {
            targetLanguage = parsed.targetLanguage;
            console.log('[Translate] Using target language from trigger message:', targetLanguage);
          }
        }

        const attachments = ctx.triggerMessage.attachments;
        const hasAttachments = hasAnalyzableFiles(attachments);


        /**
         * ── Translating a DESCRIPTION of a document is never right ──────────
         * (INCIDENT-20260812, second round)
         *
         * Both attachment paths used to read `fullText || analysis`. When a PDF
         * has no text layer there is no fullText, so the analyzer's PROSE ABOUT
         * the file became the thing translated. On a 30-page scanned medical
         * file the analyzer returned its own failure message — "No document was
         * attached or accessible. Please upload the document image or PDF" —
         * and that was translated, wrapped in a 1 KB PDF and an 8 KB DOCX, and
         * handed to the client with workflow buttons under it. It also blamed
         * the client for not attaching a file they had attached.
         *
         * #750 removed this same `||` for the truncation case. This removes it
         * for the no-text-layer case: a translation request against an
         * attachment requires the attachment's RAW TEXT, or it fails. Widening
         * a refusal-phrase regex would only lose the next race against a
         * differently-worded analyzer failure.
         */
        // copy: translate_attachment_unreadable
        const UNREADABLE_ATTACHMENT_MESSAGE =
          'I could not read any text from that file. It looks like a scanned or image-only ' +
          'PDF with no text layer, so there is nothing for me to translate yet. If you have ' +
          'a text-based version of the document, send that and I will translate it in full.';

        const failUnreadableAttachment = (
          why: string,
          existingStream?: ReturnType<typeof ctx.messaging.showPending>,
        ): StepResult => {
          console.error(`[Translate] Unreadable attachment — ${why}; refusing to translate anything else`);
          const s = existingStream ?? ctx.messaging.showPending('reading attachment');
          s.finalize({
            payload: {
              kind: 'translate.error',
              toolId: 'translate',
              severity: 'error',
              data: { errorMessage: UNREADABLE_ATTACHMENT_MESSAGE, phase: 'attachment_read' },
              summary: `translate: ${why} — no deliverable produced`,
            },
            sourceTag: 'translate_attachment_unreadable',
          });
          ctx.setState({ isTranslating: false });
          return {
            status: 'failed',
            error: new Error(UNREADABLE_ATTACHMENT_MESSAGE),
            userFacingErrorShown: true,
          };
        };

        // Store target language in state
        ctx.setState({ targetLanguage });

        let content: string | null = null;
        let title = 'Translation';
        let timestamp = new Date();
        let source: 'research' | 'formatted' | 'video_script' | 'document_analysis' | 'document_analysis_failed' | 'inline' | 'attachment' = 'inline';
        let contentMode: 'inline' | 'attachment' | 'shared_context' = 'inline';
        // True when the source text was transcribed from page images rather
        // than read from a text layer — disclosed on the coverage receipt,
        // because OCR can misread what an embedded text layer cannot.
        let fullTextFromOcr = false;

        // ====================================================================
        // PHASE 11: Chain step handling - use previous workflow output
        // When this is a chain step (e.g., deep-research → format → translate),
        // use the previous workflow's output instead of re-reading attachments
        // ====================================================================
        if (input?.isChainStep) {
          console.log('[Translate] Chain step - checking for previous workflow output');
          const previousOutput = ctx.sharedContext.lastTranslatableContentRef?.current ||
                                 ctx.sharedContext.lastTranslatableContent;
          if (previousOutput && previousOutput.content && previousOutput.content.trim()) {
            console.log('[Translate] Using previous workflow output as content');
            console.log('[Translate] Source:', previousOutput.source, `(${previousOutput.content?.length || 0} chars)`);
            content = previousOutput.content;
            title = previousOutput.title || 'Translation';
            timestamp = previousOutput.timestamp || new Date();
            source = previousOutput.source as typeof source;
            contentMode = 'shared_context';
          } else if (previousOutput) {
            console.warn('[Translate] Chain step: previous workflow output exists but has empty content — skipping');
          }
        }

        // ====================================================================
        // Mode A: Inline content provided after trigger phrase
        //
        // An analyzable attachment ALWAYS wins over inline text
        // (INCIDENT-20260812). Mode A used to run unconditionally, so a
        // message carrying both a document and a sentence the parser mistook
        // for content translated the sentence and never opened the file — a
        // 200-page PDF came back as a 2-page echo of the user's own request,
        // with no error anywhere. If someone attaches a document to a
        // translation request, the document is what they mean.
        // ====================================================================
        if (!content && hasAttachments && inlineContent.trim()) {
          console.warn(
            '[Translate] Ignoring inline content because an analyzable attachment is present ' +
            `(inline was ${inlineContent.trim().length} chars) — the document is the subject`,
          );
        }
        if (!content && inlineContent.trim() && !hasAttachments) {
          content = inlineContent.trim();
          // Use first 100 chars as title, or generate a simple one
          title = content.length > 100 ? content.slice(0, 100) + '...' : content;
          source = 'inline';
          contentMode = 'inline';
        }
        // ====================================================================
        // Mode B: Attachments provided with trigger
        // ====================================================================
        else if (!content && hasAttachments && attachments) {
          contentMode = 'attachment';
          source = 'attachment';

          // Check orchestrator cache first (prevents redundant analysis)
          // IMPORTANT: Check ref first (sync) then state (async) due to React state timing
          // documentContentRef is set synchronously in documentReader.ts, while
          // documentContent (React state) may not have propagated yet
          // 2026-08-11: prefer the RAW untruncated source text over the
          // analyzer's summary. documentContent is prose ABOUT the document,
          // built from a copy capped at 48K chars — translating it turned a
          // 200-page expediente into a 10-page "translation" that opened with
          // a paragraph describing the file. Fall back to the summary only
          // when no raw text exists (scanned PDFs on the vision path).
          // ── The cache must belong to THIS attachment (INCIDENT-20260812) ──
          // documentReader validates a fingerprint before trusting its cache;
          // this path did not. A client attached a 30-page scanned medical file
          // and received the 200-page technical manual from the PREVIOUS run,
          // because the scan produced no extractable text of its own while the
          // manual's raw text was still sitting in shared context. Wrong
          // document, no warning, delivered as their translation.
          const attachmentFingerprint = computeAttachmentFingerprint(attachments);
          const cachedFingerprint = ctx.sharedContext.documentAnalysisMeta?.fingerprint ?? '';
          const cacheMatchesAttachment = Boolean(
            attachmentFingerprint && cachedFingerprint && attachmentFingerprint === cachedFingerprint,
          );
          if (!cacheMatchesAttachment && (cachedFingerprint || attachmentFingerprint)) {
            console.warn(
              '[Translate] Ignoring cached document text — it does not belong to this attachment ' +
              `(attachment=${attachmentFingerprint || 'none'} cached=${cachedFingerprint || 'none'})`,
            );
          }
          const cachedFullText = cacheMatchesAttachment
            ? (ctx.sharedContext.documentFullTextRef?.current || ctx.sharedContext.documentFullText)
            : null;
          // Only the RAW text will do — never the analyzer's summary (see
          // UNREADABLE_ATTACHMENT_MESSAGE above).
          const cachedDocumentContent = cacheMatchesAttachment ? cachedFullText : null;
          if (cacheMatchesAttachment && !cachedFullText) {
            // This exact file has already been read and carries no text layer.
            // Re-analyzing would burn another ~25s to reach the same verdict.
            return failUnreadableAttachment('cached read of this attachment produced no raw text');
          }
          if (cachedDocumentContent) {
            fullTextFromOcr = ctx.sharedContext.documentAnalysisMeta?.fullTextSource === 'ocr' ||
                              ctx.sharedContext.documentAnalysisMeta?.fullTextSource === 'mixed';
            console.log(`[Translate] Using cached RAW document text (${cachedFullText!.length} chars${fullTextFromOcr ? ', transcribed from page images' : ''})`);
            const firstAttachment = attachments[0];
            title = firstAttachment?.name?.replace(/\.[^.]+$/, '') || 'Translated Document';
          } else {
            // No cache - analyze now (this should rarely happen with pre-orchestrator)
            console.warn('[Translate] Cache miss - analyzing documents (pre-orchestrator may have failed)');
            const stream = ctx.messaging.showPending('reading attachment');

            try {
              const analysisResult = await analyzeDocumentsWithClaude({
                files: attachments,
                userQuery: `Extract the full text content from this document for translation to ${targetLanguage}. Preserve all formatting, paragraphs, and structure.`,
                signal: ctx.abortSignal,
              });

              // Same rule as the cached path: translate the RAW document, not
              // the analyzer's description of it (2026-08-11).
              const freshFullText = analysisResult.extractedFullText?.trim();
              if (freshFullText) {
                content = freshFullText;
                fullTextFromOcr = analysisResult.fullTextSource === 'ocr' ||
                                  analysisResult.fullTextSource === 'mixed';
                console.log(`[Translate] Translating RAW extracted text (${freshFullText.length} chars${fullTextFromOcr ? ', transcribed from page images' : ''})`);
                ctx.sharedContext.setDocumentFullText?.(freshFullText);
                if (ctx.sharedContext.documentFullTextRef) {
                  ctx.sharedContext.documentFullTextRef.current = freshFullText;
                }
                // Try to extract title from first line or filename
                const firstAttachment = attachments[0];
                title = firstAttachment?.name?.replace(/\.[^.]+$/, '') || 'Translated Document';

                // Cache for other tools
                ctx.sharedContext.setDocumentContent(content);

                // Use global helper for standardized attachment read confirmation with timing/info
                finalizeAttachmentReadComplete(stream, analysisResult, ctx.recordTransaction, {
                  sourceTag: 'translate_attachment_read',
                  transactionSummary: 'document extraction for translation',
                });
              } else {
                // No raw text: never substitute the analyzer's prose, and never
                // fall through to the session-content chain (which is how a
                // scanned medical file once came back as the previous run's
                // technical manual). The attachment is the subject of the
                // request — satisfy it or fail out loud.
                return failUnreadableAttachment(
                  `attachment produced no raw text (analysis was ${
                    analysisResult.analysis?.trim().length || 0
                  } chars, discarded)`,
                  stream,
                );
              }
            } catch (err) {
              console.error('[Translate] Document analysis failed:', err);
              // Same rule and the same sentence: whether the read returned
              // nothing or threw, the client's situation is identical and so is
              // the useful advice. A failed read is a failed request — never a
              // licence to translate something else.
              return failUnreadableAttachment(
                `document analysis threw (${err instanceof Error ? err.message : 'unknown error'})`,
                stream,
              );
            }
          }
        }
        // ====================================================================
        // Mode C: No inline content, no attachments - use shared context
        // ====================================================================
        else {
          contentMode = 'shared_context';
        }

        // If no content yet (Mode C or Mode B fallback), try shared context
        if (!content) {
          const sharedContent = ctx.sharedContext.lastTranslatableContentRef.current ||
                               ctx.sharedContext.lastTranslatableContent;

          if (sharedContent && sharedContent.content && sharedContent.content.trim()) {
            content = sharedContent.content;
            title = sharedContent.title;
            timestamp = sharedContent.timestamp;
            source = sharedContent.source as typeof source;
            contentMode = 'shared_context';
          }
        }

        // If still no content, check workflowOutputStore for persisted outputs (hybrid approach)
        if (!content) {
          const storedOutput = getLatestOutputBySource('formatted') ||
                               getLatestOutputBySource('research');
          if (storedOutput) {
            content = storedOutput.content;
            title = storedOutput.title;
            timestamp = storedOutput.timestamp;
            source = storedOutput.source === 'translation' ? 'formatted' : storedOutput.source as typeof source;
            contentMode = 'shared_context';
            console.log('[Translate] Using content from workflowOutputStore:', storedOutput.id);
          }
        }

        // Fallback: Check sharedContext.documentContent (from pre-orchestrator analysis)
        if (!content) {
          const docContent = ctx.sharedContext.documentFullTextRef?.current ||
                             ctx.sharedContext.documentFullText ||
                             ctx.sharedContext.documentContentRef?.current ||
                             ctx.sharedContext.documentContent;
          if (docContent && docContent.trim()) {
            content = docContent;
            title = 'Document Analysis';
            timestamp = ctx.sharedContext.documentAnalysisMeta?.analyzedAt || new Date();
            source = 'document_analysis';
            contentMode = 'shared_context';
            console.log('[Translate] Using content from sharedContext.documentContent');
          }
        }

        // Fallback: Check documentAnalysisStore for persisted document analyses
        if (!content) {
          const latestDocAnalysis = getLatestDocumentAnalysis();
          if (latestDocAnalysis && latestDocAnalysis.analysis) {
            content = latestDocAnalysis.analysis;
            title = 'Document Analysis';
            timestamp = latestDocAnalysis.timestamp instanceof Date ? latestDocAnalysis.timestamp : new Date(latestDocAnalysis.timestamp);
            source = 'document_analysis';
            contentMode = 'shared_context';
            console.log('[Translate] Using content from documentAnalysisStore:', latestDocAnalysis.id);
          }
        }

        // Fallback: Check contentHistory for research content
        if (!content) {
          const researchContent = ctx.sharedContext.getContentBySource?.('research');
          if (researchContent && researchContent.content) {
            content = researchContent.content;
            title = researchContent.title;
            timestamp = researchContent.timestamp;
            source = 'research';
            contentMode = 'shared_context';
            console.log('[Translate] Using content from contentHistory (research)');
          }
        }

        // Check if user referenced "last message" and extract from message history
        if (!content) {
          const triggerText = ctx.triggerMessage?.text || '';
          if (/\b(?:that|the|this|last|previous)\s*(?:message)?\b/i.test(triggerText)) {
            const history = ctx.sharedContext.messageHistory || [];
            // Find the last non-user message before the current one
            const lastAssistantMessage = [...history].reverse().find(msg => !msg.isUser && msg.text);
            if (lastAssistantMessage?.text) {
              content = lastAssistantMessage.text;
              title = 'Chat Message';
              timestamp = lastAssistantMessage.timestamp instanceof Date
                ? lastAssistantMessage.timestamp
                : new Date(lastAssistantMessage.timestamp || Date.now());
              source = 'inline'; // Treat as inline since it's arbitrary text
              contentMode = 'shared_context'; // Use shared_context mode for history-sourced content
            }
          }
        }

        // Final check: do we have content?
        if (!content || !content.trim()) {
          ctx.messaging.showSpecialMessage({
            header: { title: 'no content available' },
            sections: [
              {
                type: 'text',
                content: `you can translate content to ${targetLanguage} in three ways:`,
              },
              {
                type: 'list',
                content: [
                  `type <strong>"translate to ${targetLanguage.toLowerCase()} [your text here]"</strong> to translate inline text`,
                  `attach a document and type <strong>"translate to ${targetLanguage.toLowerCase()}"</strong>`,
                  `run deep research or format a report first, then type <strong>"translate to ${targetLanguage.toLowerCase()}"</strong>`,
                ],
              },
            ],
            theme: 'purple',
          });

          return {
            status: 'completed',
            nextStep: null, // End the tool
          };
        }

        // Check if content is from document analysis (has 2-section structure)
        let documentSections: DocumentAnalysisSections | undefined;
        let isDocAnalysis = false;

        if (isDocumentAnalysisContent(content)) {
          isDocAnalysis = true;
          documentSections = parseDocumentAnalysisSections(content);
          console.log('[Translate] Detected document analysis content with sections:', {
            hasEnglishSummary: !!documentSections.englishSummary,
            hasTranscription: !!documentSections.originalTranscription,
            detectedLanguage: documentSections.metadata.language,
          });
        }

        // Store source type and content mode in state
        ctx.setState({
          sourceType: source,
          contentMode,
          isDocumentAnalysisContent: isDocAnalysis,
          documentSections,
        });

        // Continue to translation step
        return {
          status: 'completed',
          carryForward: {
            content,
            title,
            timestamp,
            source,
            contentMode,
            targetLanguage,
            isDocumentAnalysisContent: isDocAnalysis,
            documentSections,
            fullTextFromOcr,
          },
        };
      },
    },

    // ========================================================================
    // Step 2: Translate - Run Claude translator with streaming
    // ========================================================================
    {
      id: 'translate',
      name: 'Translate Content',
      activeDisplayName: 'translating document',

      execute: async (
        ctx: StepContext<TranslateState>,
        rawInput?: unknown
      ): Promise<StepResult> => {
        const input = rawInput as {
          content: string;
          title: string;
          timestamp: Date;
          source: string;
          targetLanguage: string;
          isDocumentAnalysisContent?: boolean;
          documentSections?: DocumentAnalysisSections;
          /** Source text was transcribed from page images, not a text layer. */
          fullTextFromOcr?: boolean;
        } | undefined;

        if (!input?.content) {
          return {
            status: 'failed',
            error: new Error('No content to translate'),
          };
        }

        const targetLanguage = input.targetLanguage || ctx.state.targetLanguage || 'Spanish';
        const isDocAnalysis = input.isDocumentAnalysisContent || ctx.state.isDocumentAnalysisContent;
        const documentSections = input.documentSections || ctx.state.documentSections;

        ctx.setState({ isTranslating: true });

        // Start streaming message with dynamic language
        const stream = ctx.messaging.showPending(`translating to ${targetLanguage.toLowerCase()}`);

        // Track stream state for emergency fallback
        let streamFinalized = false;

        let translatedText = '';
        let translationTokenUsage: TokenUsageMetadata | undefined;

        // Timeout for entire translation operation (not just per-chunk)
        // This catches cases where the browser tab goes dormant and timers are throttled
        const DOCUMENT_TRANSLATION_TIMEOUT_MS = TRANSLATION_TOTAL_TIMEOUT_MS;

        try {
          // ================================================================
          // DOCUMENT ANALYSIS CONTENT: Use simplified translation path
          // Keep English portions as-is, translate only foreign portions
          // ================================================================
          if (isDocAnalysis && documentSections) {
            console.log('[Translate] Using simplified path for document analysis content');
            const { englishSummary, originalTranscription, metadata } = documentSections;

            // Check if target language is English
            const targetIsEnglish = isEnglishLanguage(targetLanguage);

            // If target is English and we have an English summary, we only need to translate the transcription
            // If target is non-English, we translate the transcription to target language
            // English summary stays as-is in both cases (it's already in English)

            let translatedTranscription = '';
            let detectedLanguage = metadata.language || 'Unknown';

            if (originalTranscription && originalTranscription.trim()) {
              // Translate only the transcription portion
              // Wrap with timeout to prevent indefinite hangs during tab dormancy
              const translationPromise = translateDocumentTranscription({
                transcription: originalTranscription,
                targetLanguage,
                signal: ctx.abortSignal,
                onChunk: (_chunk, accumulated) => {
                  // Update stream activity heartbeat (for tab dormancy detection)
                  ctx.sharedContext.updateStreamActivity?.();
                  // Show progress with the current translated text. The tail
                  // widget keeps "still working" visible at the point where
                  // the content currently ends — the top banner is out of
                  // view within a page of streamed text.
                  stream.update({
                    text: accumulated || 'translating transcription…',
                    html: `<div style="white-space: pre-wrap; line-height: 1.6; font-family: system-ui;">${accumulated || 'translating transcription…'}</div>${getStreamingTailHtml(`translating to ${targetLanguage.toLowerCase()}`)}`,
                  });
                },
                onComplete: (fullText, tokenUsage) => {
                  translatedTranscription = fullText;
                  translationTokenUsage = tokenUsage;
                  // Store token usage for recording AFTER stream.finalize() (fixes timing issue)
                },
                onStreamActivity: () => {
                  ctx.sharedContext.updateStreamActivity?.();
                },
                onError: (err) => {
                  // INCIDENT-20260811: never render a raw upstream message —
                  // formatTranslationError collapses provider policy blocks to
                  // safe copy and scrubs URLs/account instructions from the rest.
                  const safeMessage = formatTranslationError(err);
                  stream.update({
                    text: `Translation error: ${safeMessage}`,
                    html: `<div style="color:#dc2626;font-weight:600;">Translation error: ${safeMessage}</div>`,
                  });
                },
              });

              // Race between translation and timeout
              const timeoutPromise = new Promise<never>((_, reject) =>
                setTimeout(() => reject(new Error(`Translation timeout after ${TRANSLATION_TIMEOUT_DESCRIPTION}`)), DOCUMENT_TRANSLATION_TIMEOUT_MS)
              );

              let transcriptionResult;
              try {
                transcriptionResult = await Promise.race([translationPromise, timeoutPromise]);
              } catch (timeoutError) {
                console.warn('[Translate] Document translation timeout - using partial content if available');
                // If we have partial content, use it; otherwise fall back to original
                if (!translatedTranscription && originalTranscription) {
                  translatedTranscription = originalTranscription;
                  console.log('[Translate] Falling back to original transcription');
                }
                transcriptionResult = { detectedLanguage };
              }

              detectedLanguage = transcriptionResult.detectedLanguage || detectedLanguage;
            }

            // Combine sections in original order:
            // 1. English summary (kept as-is - already in English)
            // 2. Translated transcription (was in source language, now in target language)
            const unifiedOutput = [
              englishSummary,
              translatedTranscription,
            ].filter(Boolean).join('\n\n');

            // Store detected source language
            ctx.setState({ detectedSourceLanguage: detectedLanguage });

            // Build lineage tokenUsages: upstream + this translation step
            const translateUpstreamUsages = ctx.sharedContext.lastTranslatableContentRef?.current?.tokenUsages || [];
            const translateLineageUsages = [
              ...translateUpstreamUsages,
              ...(translationTokenUsage ? [{ ...translationTokenUsage, summary: `translated to ${targetLanguage.toLowerCase()}` }] : []),
            ];

            // Store in workflowOutputStore for downstream workflows
            const storedOutputId = storeWorkflowOutput({
              content: unifiedOutput,
              title: input.title,
              timestamp: new Date(),
              source: 'translation',
              tokenUsages: translateLineageUsages.length > 0 ? translateLineageUsages : undefined,
            });

            // Set translatable content for downstream workflows
            const translatedTranslatableData = {
              content: unifiedOutput,
              title: input.title,
              timestamp: new Date(),
              source: 'formatted' as const,
              tokenUsages: translateLineageUsages.length > 0 ? translateLineageUsages : undefined,
            };
            ctx.sharedContext.setLastTranslatableContent(translatedTranslatableData);
            ctx.sharedContext.lastTranslatableContentRef.current = translatedTranslatableData;

            // Finalize with unified output - use formatDocumentToHtml for proper rendering
            // This formats tables properly and removes markdown artifacts
            const formattedHtml = isDocumentContent(unifiedOutput)
              ? formatDocumentToHtml(unifiedOutput)
              : formatResearchToHtml(unifiedOutput);

            stream.finalize({
              text: unifiedOutput,
              html: formattedHtml,
              sourceTag: 'translation_result',
              tokenUsages: translateLineageUsages.length > 0 ? translateLineageUsages : undefined,
              tokenUsage: translationTokenUsage ? { ...translationTokenUsage, summary: `translated to ${targetLanguage.toLowerCase()}` } : undefined,
              workflowOutput: {
                type: 'translation',
                content: unifiedOutput,
                title: input.title,
                timestamp: new Date(),
                sourceWorkflow: 'translate',
              },
            });
            streamFinalized = true;

            // Record transaction AFTER message display (fixes timing issue)
            if (translationTokenUsage) {
              ctx.recordTransaction({
                tokenUsage: translationTokenUsage,
                source: 'agenticWorkflows',
                modelOverride: buildModelDisplay(translationTokenUsage, 'Translation', CLAUDE_TRANSLATOR_DISPLAY),
                summary: `translated to ${targetLanguage.toLowerCase()}`,
              });
            }

            // Update state
            ctx.setState({
              isTranslating: false,
              translatedContent: unifiedOutput,
              translatedTitle: input.title,
              tokenUsage: translationTokenUsage,
            });

            return {
              status: 'completed',
              output: {
                translatedContent: unifiedOutput,
                translatedTitle: input.title,
                detectedSourceLanguage: detectedLanguage,
                tokenUsage: translationTokenUsage,
              },
            };
          }

          // ================================================================
          // STANDARD CONTENT: Use full manifest-based translation
          // ================================================================
          // Build translation manifest
          // Map 'inline' and 'attachment' sources to 'formatted' for manifest building
          const manifestSource = (input.source === 'inline' || input.source === 'attachment')
            ? 'formatted'
            : (input.source as 'research' | 'formatted' | 'video_script');

          // MT-QA-BUG-001: thread tenant firm name into the translation
          // manifest so T1/T2 translated PDFs/DOCXs don't inherit Fernando
          // branding.
          const translateTenantBranding = await fetchCurrentTenantBranding();

          const manifest = buildTranslationManifest({
            title: input.title,
            content: input.content,
            createdAt: input.timestamp,
            source: manifestSource,
            firmName: translateTenantBranding.firmName,
          });

          const manifestText = formatManifestForTranslation(manifest);

          // Live position of a segmented run. A 200-page document spends
          // several minutes with nothing visibly changing; the section count
          // is the difference between "working" and "stuck".
          let sectionProgress: { completedSections: number; totalSections: number } | null = null;
          const progressBanner = () =>
            sectionProgress && sectionProgress.totalSections > 1
              ? getSectionProgressHtml(
                  `translating to ${targetLanguage.toLowerCase()}`,
                  sectionProgress.completedSections,
                  sectionProgress.totalSections,
                )
              : '';
          let lastPreview = '';
          // Live preview cap (2026-08-22): repainting the WHOLE accumulated
          // document on every chunk grew the DOM without limit and re-parsed
          // it all through DOMParser each paint. The reader watches the
          // GROWING EDGE, so stream the tail; the full document arrives at
          // finalize and in the PDF/DOCX deliverables.
          const LIVE_PREVIEW_TAIL_CHARS = 4000;
          const previewTail = (text: string) =>
            text.length > LIVE_PREVIEW_TAIL_CHARS
              ? `… (earlier sections omitted while translating) …\n\n${text.slice(-LIVE_PREVIEW_TAIL_CHARS)}`
              : text;
          const paintProgress = () => {
            // Position label carried into the TAIL widget too: the banner at
            // the top scrolls out of view within a page, and the bottom of the
            // growing text is where a reader actually is. Without an animated
            // affordance THERE, a live 110-page run reads as "cut off and
            // stopped" (Fernando, 2026-08-13).
            const tailLabel = sectionProgress && sectionProgress.totalSections > 1
              ? `translating to ${targetLanguage.toLowerCase()} · section ${Math.min(sectionProgress.completedSections + 1, sectionProgress.totalSections)} of ${sectionProgress.totalSections}`
              : `translating to ${targetLanguage.toLowerCase()}`;
            stream.update({
              text: lastPreview || 'translating document metadata…',
              html: lastPreview
                ? `${progressBanner()}${formatResearchToHtml(previewTail(lastPreview))}${getStreamingTailHtml(tailLabel)}`
                : `${progressBanner()}${getPendingWidgetHtml('translating document metadata')}`,
            });
          };

          const translationRun = await runTranslation({
            documentContent: manifestText,
            targetLanguage,
            onProgress: (progress) => {
              // Sections land in bursts with long quiet gaps between them, so
              // repaint on progress too - not only when new text arrives.
              sectionProgress = progress;
              // Section completion is liveness. Without this heartbeat the
              // orchestrator's inactivity ceiling can only see text chunks,
              // and a between-sections gap reads as a dead stream.
              ctx.sharedContext.updateStreamActivity?.();
              paintProgress();
            },
            // Token-level liveness (2026-08-14). Section COMPLETIONS are too
            // sparse: 25 sections at concurrency 6 finish in waves, so a
            // 200-page run went minutes with no callback and the orchestrator
            // ceiling killed it mid-run at section 14 of 25 — twice. Every
            // delta from every in-flight section now counts as activity.
            onSegmentActivity: () => {
              ctx.sharedContext.updateStreamActivity?.();
            },
            // BYTES, not content. Content deltas go silent for minutes while a
            // high-effort model reasons, and the provider's keep-alives carry
            // no content at all — so a run that was demonstrably alive on the
            // wire looked dead to the orchestrator and was aborted three
            // times (INCIDENT-20260815).
            onStreamActivity: () => {
              ctx.sharedContext.updateStreamActivity?.();
            },
            onChunk: (_chunk, accumulated) => {
              // Update stream activity heartbeat (for tab dormancy detection)
              ctx.sharedContext.updateStreamActivity?.();
              lastPreview = extractBodyContentFromPartialTranslation(accumulated) || lastPreview;
              paintProgress();
            },
            onComplete: (fullText, tokenUsage) => {
              translatedText = fullText;
              translationTokenUsage = tokenUsage;
              // Store token usage for recording AFTER stream.finalize() (fixes timing issue)
            },
            onError: (err) => {
              // INCIDENT-20260811: see above — sanitized rendering only.
              const safeMessage = formatTranslationError(err);
              stream.update({
                text: `Translation error: ${safeMessage}`,
                html: `<div style="color:#dc2626;font-weight:600;">Translation error: ${safeMessage}</div>`,
              });
            },
          });

          // Parse the translated manifest
          const translated = parseTranslatedManifest(translatedText);

          // Last line of defence before a client file is built: if what came
          // back is the model ARGUING rather than translating, do not turn it
          // into a PDF and a DOCX (INCIDENT-20260812 — a mis-detected target
          // language produced "'Scanned' is a document format, not a language…"
          // and it was delivered as the client's translated document).
          if (isRefusalShapedTranslation(translated.bodyContent)) {
            const refused =
              'The translation did not go through — the model reported a problem with the ' +
              'request rather than returning a translation. My team has been alerted. ' +
              'Please try again, naming the target language explicitly.';
            console.error('[Translate] Refusal-shaped output blocked before deliverable creation');
            stream.finalize({
              payload: {
                kind: 'translate.error',
                toolId: 'translate',
                severity: 'error',
                data: { errorMessage: refused, phase: 'translation' },
                summary: 'translate: refusal-shaped model output blocked before deliverable creation',
              },
              sourceTag: 'translate_refusal_blocked',
            });
            streamFinalized = true;
            ctx.setState({ isTranslating: false });
            return { status: 'failed', error: new Error(refused), userFacingErrorShown: true };
          }

          // Second line of defence: is this the WHOLE translation?
          //
          // INCIDENT-20260825. A 30-page clinical record shipped from THIS
          // lane as a 3KB PDF and an 8KB DOCX. The equivalent gate had been
          // added to the backend deliverable hook the day before and never
          // here — and the pull request that added it had itself warned that
          // two copies of one rule drifting apart is how code rots.
          //
          // The files are withheld, never quietly shrunk: a plausible short
          // file is more damaging than no file. The translated text still
          // reaches the user inline — only the DELIVERABLE is held back.
          //
          // WHAT THIS DOES NOT CATCH, stated plainly so nobody trusts it too
          // far: it compares the output against the text the TRANSLATOR was
          // given, so it catches loss inside the translation step and nothing
          // upstream of it. When the READER hands over a fragment — the
          // 2026-08-25 run, where one page of a 30-page scan reached the
          // translator — the ratio is close to 1 and this gate passes,
          // correctly, because the translation of that fragment IS complete.
          // The reader is where that class has to be caught, which is what
          // the page-coverage gate in claudeDocumentAnalyzer is for.
          const completeness = assessTranslationCompleteness(manifestText, translated.bodyContent);
          if (!completeness.complete) {
            const held =
              `I translated what reached me, but it is far too little to be the whole document — ` +
              `about ${Math.round(completeness.ratio * 100)}% of the source came through. ` +
              `I have not attached a PDF or Word file, because a file that looks finished and is not ` +
              `is worse than no file. The translated text above is everything I actually received. ` +
              `Re-send the document and I will read it again.`;
            console.error(
              `[Translate] Deliverable withheld — ${completeness.reason} ` +
              `(source ${manifestText.length} chars, output ${translated.bodyContent.length} chars)`,
            );
            stream.finalize({
              text: translated.bodyContent,
              payload: {
                kind: 'translate.error',
                toolId: 'translate',
                severity: 'error',
                data: { errorMessage: held, phase: 'deliverable' },
                summary: `translate: deliverable withheld (${completeness.reason})`,
              },
              sourceTag: 'translate_incomplete_blocked',
            });
            streamFinalized = true;
            ctx.setState({ isTranslating: false });
            return { status: 'failed', error: new Error(held), userFacingErrorShown: true };
          }

          // Store detected source language
          ctx.setState({ detectedSourceLanguage: translated.detectedSourceLanguage });

          // Build translated PDF and DOCX with Firebase Storage upload for cross-device persistence
          const auth = getAuth();
          const currentUid = auth.currentUser?.uid;

          // ── SAVE THE TRANSLATION BEFORE BUILDING ANYTHING (2026-08-20) ────
          // The 200-page manual finally reached section 25 of 25 and then sat
          // at 100% forever; a refresh lost the whole thing. Everything that
          // PERSISTS a turn — storeWorkflowOutput, stream.finalize — used to
          // run only AFTER figure extraction and PDF/DOCX generation, so the
          // translated text existed nowhere but this closure while minutes of
          // main-thread document building went by.
          //
          // The translation is the expensive, irreplaceable artifact: twenty
          // minutes of model time. A PDF can be rebuilt from it in seconds. So
          // it is banked FIRST, and the deliverables attach to it afterwards.
          // A refresh, a crash, or a failure in the builder can now cost at
          // most the files, never the words.
          ctx.sharedContext.setLastTranslatableContent({
            content: translated.bodyContent,
            title: translated.title || input.title,
            timestamp: new Date(),
            source: 'formatted' as const,
          });
          const earlyOutputId = storeWorkflowOutput({
            content: translated.bodyContent,
            title: translated.title || input.title,
            timestamp: new Date(),
            source: 'translation',
          });
          console.log(`[Translate] Banked translated text before deliverable build (${translated.bodyContent.length} chars, output ${earlyOutputId})`);

          // The widget sat at 100% with no further update through both of the
          // long awaits below, which is what "stuck at 100%" was. Translation
          // being finished is not the turn being finished; say what is
          // actually happening.
          const paintBuildStage = (label: string) => {
            try {
              stream.update({
                text: lastPreview || translated.bodyContent.slice(0, 2000),
                html: `${getSectionProgressHtml(label, sectionProgress?.totalSections ?? 1, sectionProgress?.totalSections ?? 1)}${formatResearchToHtml(translated.bodyContent.slice(0, 4000))}${getStreamingTailHtml(label)}`,
              });
              ctx.sharedContext.updateStreamActivity?.();
            } catch { /* a progress paint must never break the turn */ }
          };

          // Figures from the source document, so the deliverable keeps its
          // charts (2026-08-13: 35 figures in, zero out). Fail-safe: returns []
          // when extraction is unavailable, which is exactly today's behaviour.
          let sourceImages: SourcePageImage[] = [];
          const figureSources = (ctx.triggerMessage.attachments || []).filter(
            (f) => /\.pdf$/i.test(f.name) || f.type === 'application/pdf',
          );
          // A SCANNED source has no figures — it has page photographs, and its
          // text came from reading them. Carrying those images into the
          // deliverable put the untranslated Spanish original on every second
          // page of the 30-page clinical record (2026-08-21).
          if (input.fullTextFromOcr) {
            console.log('[Translate] Source was transcribed from page images — no figures to carry');
          } else if (figureSources.length > 0) {
            paintBuildStage('collecting figures from the source');
            for (const pdfFile of figureSources) {
              sourceImages = sourceImages.concat(
                await extractPdfImagesClientSide(pdfFile, undefined, {
                  // Page-by-page, so a slow extraction reads as progress rather
                  // than as a frozen 100%, and so the liveness heartbeat keeps
                  // beating through it (paintBuildStage calls
                  // updateStreamActivity). Repainting every page of a 200-page
                  // document is wasted work, so throttle to a readable cadence.
                  onProgress: ({ pageNumber, totalPages, figures }) => {
                    if (pageNumber % 5 !== 0 && pageNumber !== totalPages) return;
                    paintBuildStage(
                      `collecting figures from the source \u00b7 page ${pageNumber} of ${totalPages}` +
                        (figures > 0 ? ` \u00b7 ${figures} found` : ''),
                    );
                  },
                }),
              );
            }
            console.log(`[Translate] Carrying ${sourceImages.length} source figure(s) into the deliverable`);
          }
          paintBuildStage('building your PDF and Word file');

          const {
            pdf: translatedPdf,
            docx: translatedDocx,
            pdfSerialized,
            docxSerialized,
            pdfSerializedWithStorage,
            docxSerializedWithStorage,
            uploadStatus,
          } = await generateDualFormatDocumentsWithStorage(
            {
              type: 'formatted',
              content: translated.bodyContent,
              title: input.title,
              createdAt: input.timestamp,
              // The user's own document already has a title page and running
              // header; ours produced the "broken first page" and shifted every
              // page by one (2026-08-13 formatting feedback). Only generate a
              // cover page for content we authored.
              includeCoverPage:
                input.source !== 'attachment' && input.source !== 'document_analysis',
              sourceImages,
              // Building the files and saving them are three long steps that
              // used to sit behind one motionless label (2026-08-21 sweep).
              onProgress: ({ stage, pages }) => {
                if (stage === 'saving') { paintBuildStage('saving your files'); return; }
                paintBuildStage(pages
                  ? `building your PDF and Word file \u00b7 page ${pages}`
                  : 'building your PDF and Word file');
              },
              translations: {
                title: translated.title,
                date: translated.date,
                organizationName: translated.firmName,
                copyrightNotice: translated.copyrightNotice,
                copyrightShort: translated.copyrightShort,
                pageNumberFormat: translated.pageNumberFormat,
              },
            },
            // Only upload to Storage if user is authenticated
            currentUid ? { uid: currentUid, messageId: `tr-${Date.now()}` } : undefined
          );

          // Log upload status for debugging cross-device persistence issues
          if (uploadStatus) {
            if (uploadStatus.error) {
              console.warn('[Translate] Storage upload issue:', uploadStatus.error);
            } else if (uploadStatus.pdfUploaded && uploadStatus.docxUploaded) {
              console.log('[Translate] Storage upload successful - attachments will persist cross-device');
            }
          }

          // Use Storage-backed serialized attachments if available, fallback to localStorage-only
          const finalPdfSerialized: SerializedAttachment | undefined = pdfSerializedWithStorage || pdfSerialized;
          const finalDocxSerialized: SerializedAttachment | undefined = docxSerializedWithStorage || docxSerialized;

          // Build lineage tokenUsages: upstream + this translation step
          const translateUpstreamUsages = ctx.sharedContext.lastTranslatableContentRef?.current?.tokenUsages || [];
          const translateLineageUsages = [
            ...translateUpstreamUsages,
            ...(translationTokenUsage ? [{ ...translationTokenUsage, summary: `translated to ${targetLanguage.toLowerCase()}` }] : []),
          ];

          // Store in workflowOutputStore for downstream workflows
          // Reuses the id banked before the build so this UPDATES that entry
          // instead of writing a second ~500K-char copy into a 10-slot,
          // 5MB-bounded store.
          const storedOutputId = storeWorkflowOutput({
            id: earlyOutputId,
            content: translated.bodyContent,
            title: translated.title || input.title,
            timestamp: new Date(),
            source: 'translation',
            tokenUsages: translateLineageUsages.length > 0 ? translateLineageUsages : undefined,
          });

          // Set translatable content with the translated content for downstream workflows
          const translatedTranslatableData = {
            content: translated.bodyContent,
            title: translated.title || input.title,
            timestamp: new Date(),
            source: 'formatted' as const,
            tokenUsages: translateLineageUsages.length > 0 ? translateLineageUsages : undefined,
          };
          ctx.sharedContext.setLastTranslatableContent(translatedTranslatableData);
          ctx.sharedContext.lastTranslatableContentRef.current = translatedTranslatableData;

          // Finalize the streaming message
          // Use document formatter for document-style content, fallback to research formatter
          const documentHtml = isDocumentContent(translated.bodyContent)
            ? formatDocumentToHtml(translated.bodyContent)
            : formatResearchToHtml(translated.bodyContent);
          // The receipt rides the HTML only. `text` stays the pure document
          // because it feeds the PDF/DOCX builders and every downstream
          // workflow - a status line must never end up inside the deliverable.
          const coverageHtml = translationRun?.coverage
            ? getTranslationCoverageHtml(translationRun.coverage, { fromOcr: Boolean(input.fullTextFromOcr) })
            : '';
          const finalHtml = `${coverageHtml}${documentHtml}`;
          stream.finalize({
            text: translated.bodyContent,
            html: finalHtml,
            sourceTag: 'translation_result',
            attachments: [translatedPdf, translatedDocx],
            attachmentsSerialized: finalPdfSerialized && finalDocxSerialized
              ? [finalPdfSerialized, finalDocxSerialized]
              : [pdfSerialized, docxSerialized],
            tokenUsages: translateLineageUsages.length > 0 ? translateLineageUsages : undefined,
            tokenUsage: translationTokenUsage ? { ...translationTokenUsage, summary: `translated to ${targetLanguage.toLowerCase()}` } : undefined,
            workflowOutput: {
              type: 'translation',
              content: translated.bodyContent,
              title: translated.title || input.title,
              timestamp: new Date(),
              sourceWorkflow: 'translate',
            },
          });
          streamFinalized = true;

          // Record transaction AFTER message display (fixes timing issue)
          if (translationTokenUsage) {
            ctx.recordTransaction({
              tokenUsage: translationTokenUsage,
              source: 'agenticWorkflows',
              modelOverride: buildModelDisplay(translationTokenUsage, 'Translation', CLAUDE_TRANSLATOR_DISPLAY),
              summary: `translated to ${targetLanguage.toLowerCase()}`,
            });
          }

          // Update state
          ctx.setState({
            isTranslating: false,
            translatedContent: translated.bodyContent,
            translatedTitle: translated.title,
            detectedSourceLanguage: translated.detectedSourceLanguage,
            tokenUsage: translationTokenUsage,
          });

          return {
            status: 'completed',
            output: {
              translatedContent: translated.bodyContent,
              translatedTitle: translated.title,
              detectedSourceLanguage: translated.detectedSourceLanguage,
              tokenUsage: translationTokenUsage,
            },
          };
        } catch (error) {
          ctx.setState({ isTranslating: false });
          // INCIDENT-20260811: this string is rendered to the customer AND
          // summarized into the transcript, so it must be the sanitized form —
          // a raw upstream body is how the OpenRouter age-gate errand reached
          // a Fernando client's screen.
          const errorMessage = error ? formatTranslationError(error) : 'Unknown error';

          // Show error to user instead of silently aborting (renderer-composed)
          stream.finalize({
            payload: {
              kind: 'translate.error',
              toolId: 'translate',
              severity: 'error',
              data: { errorMessage, phase: 'translation' },
              summary: `translate: failed — ${errorMessage}`,
            },
            sourceTag: 'translate_error',
          });
          streamFinalized = true;

          return {
            status: 'failed',
            error: new Error(`Unable to translate content: ${formatTranslationError(error)}`),
            userFacingErrorShown: true, // stream.finalize already showed the error — prevent runtime from adding a duplicate "Step Failed" tile
          };
        } finally {
          // EMERGENCY SAFETY NET: Ensure stream is always finalized
          // This catches cases where an exception occurs before any finalize() call
          // (e.g., tab dormancy causing timeout, unexpected parsing errors, etc.)
          if (!streamFinalized) {
            console.error('[Translate] EMERGENCY: Stream not finalized - forcing fallback');
            try {
              stream.finalize({
                payload: {
                  kind: 'translate.error',
                  toolId: 'translate',
                  severity: 'error',
                  data: { errorMessage: 'translation encountered an unexpected issue', phase: 'emergency_fallback' },
                  summary: 'translate: emergency fallback fired (no specific cause captured)',
                },
                sourceTag: 'translate_emergency_fallback',
              });
            } catch (emergencyError) {
              console.error('[Translate] Even emergency finalization failed:', emergencyError);
            }
          }

          // Always clear state and active stream reference
          ctx.setState({ isTranslating: false });
          ctx.sharedContext.setActiveStream?.(null);
        }
      },

      cleanup: async (ctx: StepContext<TranslateState>) => {
        ctx.setState({ isTranslating: false });
      },
    },
  ],

  isAuthorized: isAgenticWorkflowsAuthorized,

  theme: TRANSLATE_THEME,

  buttonIds: BUTTON_IDS,

  canTriggerFromButton: true,
};

// ============================================================================
// Export
// ============================================================================

export default translateTool;
